import { type FC, type PropsWithChildren, useEffect, useState } from 'react';

import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { openTelegramLink } from '@/shared/helpers/tg-links/open-tg-link';
import { Lottie } from '@/shared/hocs/lottieWrapper';
import { useTelegramMainButton } from '@/shared/hooks/telegram/use-telegram-main-button';

import animationData from '@/assets/lotties/loader.lottie?url';

import s from './index.module.scss';

export enum LoaderSizes {
  MEDIUM = 'medium',
  LARGE = 'large',
}

interface Props {
  className?: string;
  size?: LoaderSizes;
  withDelayMessage?: boolean;
}

const DELAY = 3000;
const PDCK_COMMUNITY_LINK = 'https://t.me/playcoin';

const DelayedInvite = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();

  useTelegramMainButton(
    () => openTelegramLink(PDCK_COMMUNITY_LINK),
    t('Visit our community')
  );

  return (
    <div className={s.invite}>
      {t('While PlayDeck is loading you can')}
      {children}
    </div>
  );
};

export const Loader: FC<PropsWithChildren<Props>> = ({
  className,
  size = LoaderSizes.LARGE,
  withDelayMessage = false,
  children,
}) => {
  const [showDelayedInvite, setShowDelayedInvite] = useState<boolean>(false);

  useEffect(() => {
    if (!withDelayMessage) return;

    const timeout = setTimeout(() => {
      setShowDelayedInvite(true);
    }, DELAY);

    return () => {
      return clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={clsx(s.container, s[size], className)}>
      <Lottie className={s.loader} src={animationData} loop autoplay />
      {showDelayedInvite && <DelayedInvite>{children}</DelayedInvite>}
    </div>
  );
};
